/**
 * AI Quota Service
 *
 * Manages AI usage quotas for organizations based on subscription tier.
 *
 * Free Tier: Lifetime starter credits (50 images, 250 TTS, 5 videos)
 *   - Credits never reset - once exhausted, upgrade is required
 *
 * Ministry Tier ($24.99/mo): Monthly rolling quota (250 images, 1000 TTS, 10 videos)
 *   - Resets on the 1st of each month
 */

import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore/lite';
import { Organization, MediaSourceType, AIQuota, TIER_LIMITS, SubscriptionTier } from '../types';
import { saveMediaItem } from './firebase';

/**
 * Get current month in YYYY-MM format
 */
const getCurrentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Get organization's subscription tier
 */
const getOrgTier = (org: Organization): SubscriptionTier => {
  return org.subscription?.tier || 'free';
};

/**
 * Get tier limits for an organization
 */
export const getTierLimits = (org: Organization) => {
  const tier = getOrgTier(org);
  return TIER_LIMITS[tier];
};

/**
 * Check if quota needs reset (only for ministry tier - monthly reset)
 */
const needsMonthlyReset = (quota: AIQuota, tier: SubscriptionTier): boolean => {
  // Free tier never resets - lifetime credits
  if (tier === 'free') return false;

  // Ministry tier resets monthly
  const currentMonth = getCurrentMonth();
  return quota.period !== currentMonth;
};

/**
 * Get organization's current AI quota status
 */
export const getQuotaStatus = async (orgId: string): Promise<{
  tier: SubscriptionTier;
  period: string; // 'lifetime' or 'YYYY-MM'
  isLifetime: boolean;
  images: { used: number; limit: number; remaining: number };
  tts: { used: number; limit: number; remaining: number };
  videos: { used: number; limit: number; remaining: number };
  canUseImage: boolean;
  canUseTTS: boolean;
  canUseVideo: boolean;
}> => {
  try {
    const db = getFirestore();
    const orgRef = doc(db, 'organizations', orgId);
    const orgSnap = await getDoc(orgRef);

    if (!orgSnap.exists()) {
      throw new Error('Organization not found');
    }

    const org = orgSnap.data() as Organization;
    const tier = getOrgTier(org);
    const limits = TIER_LIMITS[tier];
    const isLifetime = limits.isLifetime;
    const period = isLifetime ? 'lifetime' : getCurrentMonth();

    // If no quota data, return fresh quotas
    if (!org.aiQuota) {
      return {
        tier,
        period,
        isLifetime,
        images: { used: 0, limit: limits.images, remaining: limits.images },
        tts: { used: 0, limit: limits.tts, remaining: limits.tts },
        videos: { used: 0, limit: limits.videos, remaining: limits.videos },
        canUseImage: limits.images > 0,
        canUseTTS: limits.tts > 0,
        canUseVideo: limits.videos > 0,
      };
    }

    // Check if ministry tier needs monthly reset
    if (needsMonthlyReset(org.aiQuota, tier)) {
      return {
        tier,
        period,
        isLifetime,
        images: { used: 0, limit: limits.images, remaining: limits.images },
        tts: { used: 0, limit: limits.tts, remaining: limits.tts },
        videos: { used: 0, limit: limits.videos, remaining: limits.videos },
        canUseImage: limits.images > 0,
        canUseTTS: limits.tts > 0,
        canUseVideo: limits.videos > 0,
      };
    }

    const quota = org.aiQuota;
    const imageCount = quota.imageCount || 0;
    const ttsCount = quota.ttsCount || 0;
    const videoCount = quota.videoCount || 0;
    return {
      tier,
      period,
      isLifetime,
      images: {
        used: imageCount,
        limit: limits.images,
        remaining: Math.max(0, limits.images - imageCount),
      },
      tts: {
        used: ttsCount,
        limit: limits.tts,
        remaining: Math.max(0, limits.tts - ttsCount),
      },
      videos: {
        used: videoCount,
        limit: limits.videos,
        remaining: Math.max(0, limits.videos - videoCount),
      },
      canUseImage: imageCount < limits.images,
      canUseTTS: ttsCount < limits.tts,
      canUseVideo: videoCount < limits.videos,
    };
  } catch (error) {
    console.error('Error getting quota status:', error);
    throw error;
  }
};

// Alias for backwards compatibility
export const getMonthlyQuotaStatus = getQuotaStatus;

/**
 * Check if organization can use AI feature
 * Throws error if quota exceeded
 */
export const checkAIQuota = async (orgId: string, feature: 'gemini-image' | 'google-tts' | 'veo-video'): Promise<void> => {
  const status = await getQuotaStatus(orgId);
  const upgradeMessage = status.tier === 'free'
    ? ' Upgrade to Ministry tier ($24.99/month) for monthly rolling credits.'
    : '';

  if (feature === 'gemini-image') {
    if (!status.canUseImage) {
      if (status.isLifetime) {
        throw new Error(
          `You've used all ${status.images.limit} of your free image credits.${upgradeMessage}`
        );
      } else {
        throw new Error(
          `Monthly image quota exceeded (${status.images.used}/${status.images.limit}). Resets on the 1st of next month.`
        );
      }
    }
  } else if (feature === 'google-tts') {
    if (!status.canUseTTS) {
      if (status.isLifetime) {
        throw new Error(
          `You've used all ${status.tts.limit} of your free audio credits.${upgradeMessage}`
        );
      } else {
        throw new Error(
          `Monthly audio quota exceeded (${status.tts.used}/${status.tts.limit}). Resets on the 1st of next month.`
        );
      }
    }
  } else if (feature === 'veo-video') {
    if (!status.canUseVideo) {
      if (status.isLifetime) {
        throw new Error(
          `You've used all ${status.videos.limit} of your free video credits.${upgradeMessage}`
        );
      } else {
        throw new Error(
          `Monthly video quota exceeded (${status.videos.used}/${status.videos.limit}). Resets on the 1st of next month.`
        );
      }
    }
  }
};

/**
 * Media metadata for auto-saving to media library
 */
export interface MediaMetadata {
  storagePath: string;
  prompt?: string;
  sourceType: MediaSourceType;
  associatedStudyId?: string;
  associatedCourseId?: string;
  segmentId?: string;
  fileName?: string;
  fileSize?: number;
  dimensions?: { width: number; height: number };
  duration?: number;
}

/**
 * Increment AI quota usage for an organization
 * Optionally saves media to the media library when mediaMetadata is provided
 * @returns The media item ID if saved to library, otherwise void
 */
export const incrementAIQuota = async (
  orgId: string,
  userId: string,
  feature: 'gemini-image' | 'google-tts' | 'veo-video',
  details?: string,
  imageUrl?: string,
  audioUrl?: string,
  videoUrl?: string,
  mediaMetadata?: MediaMetadata
): Promise<string | void> => {
  try {
    const db = getFirestore();
    const orgRef = doc(db, 'organizations', orgId);
    const orgSnap = await getDoc(orgRef);

    if (!orgSnap.exists()) {
      throw new Error('Organization not found');
    }

    const org = orgSnap.data() as Organization;
    const tier = getOrgTier(org);
    const limits = TIER_LIMITS[tier];
    const isLifetime = limits.isLifetime;
    const period = isLifetime ? 'lifetime' : getCurrentMonth();
    const now = Date.now();

    const logEntry: {
      timestamp: number;
      userId: string;
      feature: 'gemini-image' | 'google-tts' | 'veo-video';
      details?: string;
      imageUrl?: string;
      audioUrl?: string;
      videoUrl?: string;
    } = {
      timestamp: now,
      userId,
      feature,
    };
    if (details) logEntry.details = details;
    if (imageUrl) logEntry.imageUrl = imageUrl;
    if (audioUrl) logEntry.audioUrl = audioUrl;
    if (videoUrl) logEntry.videoUrl = videoUrl;

    // Check if we need to reset (only for ministry tier monthly reset)
    const shouldReset = org.aiQuota && needsMonthlyReset(org.aiQuota, tier);

    let newQuota: AIQuota;

    if (!org.aiQuota || shouldReset) {
      // Initialize or reset quota
      newQuota = {
        period,
        imageCount: feature === 'gemini-image' ? 1 : 0,
        ttsCount: feature === 'google-tts' ? 1 : 0,
        videoCount: feature === 'veo-video' ? 1 : 0,
        lastResetAt: now,
        usageLog: [logEntry]
      };
    } else {
      // Increment existing quota
      const existingLog = org.aiQuota.usageLog || [];

      newQuota = {
        period: org.aiQuota.period, // Keep existing period (lifetime stays lifetime)
        imageCount: org.aiQuota.imageCount + (feature === 'gemini-image' ? 1 : 0),
        ttsCount: org.aiQuota.ttsCount + (feature === 'google-tts' ? 1 : 0),
        videoCount: org.aiQuota.videoCount + (feature === 'veo-video' ? 1 : 0),
        lastResetAt: org.aiQuota.lastResetAt,
        usageLog: [...existingLog, logEntry]
      };
    }

    // Update organization document
    await updateDoc(orgRef, {
      aiQuota: newQuota
    });

    const periodLabel = isLifetime ? 'lifetime' : 'this month';
    console.log(`AI quota incremented for org ${orgId} (${tier} tier, ${periodLabel}): images=${newQuota.imageCount}/${limits.images}, tts=${newQuota.ttsCount}/${limits.tts}, videos=${newQuota.videoCount}/${limits.videos}`);

    // Auto-save to media library if metadata is provided
    if (mediaMetadata) {
      const mediaType: 'image' | 'audio' | 'video' =
        feature === 'gemini-image' ? 'image' :
        feature === 'google-tts' ? 'audio' : 'video';

      const mediaUrl = imageUrl || audioUrl || videoUrl || '';

      const mediaItem: Parameters<typeof saveMediaItem>[0] = {
        orgId,
        mediaType,
        url: mediaUrl,
        storagePath: mediaMetadata.storagePath,
        uploadedBy: userId,
        uploadedAt: now,
        isAIGenerated: true,
        sourceType: mediaMetadata.sourceType,
      };

      if (mediaMetadata.prompt !== undefined) mediaItem.prompt = mediaMetadata.prompt;
      if (mediaMetadata.associatedStudyId !== undefined) mediaItem.associatedStudyId = mediaMetadata.associatedStudyId;
      if (mediaMetadata.associatedCourseId !== undefined) mediaItem.associatedCourseId = mediaMetadata.associatedCourseId;
      if (mediaMetadata.segmentId !== undefined) mediaItem.segmentId = mediaMetadata.segmentId;
      if (mediaMetadata.fileName !== undefined) mediaItem.fileName = mediaMetadata.fileName;
      if (mediaMetadata.fileSize !== undefined) mediaItem.fileSize = mediaMetadata.fileSize;
      if (mediaMetadata.dimensions !== undefined) mediaItem.dimensions = mediaMetadata.dimensions;
      if (mediaMetadata.duration !== undefined) mediaItem.duration = mediaMetadata.duration;

      const mediaId = await saveMediaItem(mediaItem);
      console.log(`Media saved to library: ${mediaId}`);
      return mediaId;
    }
  } catch (error) {
    console.error('Error incrementing AI quota:', error);
    throw error;
  }
};

/**
 * Get AI quota usage report for an organization
 */
export const getAIQuotaReport = async (orgId: string): Promise<{
  tier: SubscriptionTier;
  period: string;
  isLifetime: boolean;
  images: { used: number; limit: number; remaining: number };
  tts: { used: number; limit: number; remaining: number };
  videos: { used: number; limit: number; remaining: number };
  recentUsage: Array<{
    timestamp: number;
    userId: string;
    feature: string;
    details?: string;
    imageUrl?: string;
    audioUrl?: string;
    videoUrl?: string;
  }>;
}> => {
  const status = await getQuotaStatus(orgId);
  const db = getFirestore();
  const orgRef = doc(db, 'organizations', orgId);
  const orgSnap = await getDoc(orgRef);

  if (!orgSnap.exists()) {
    throw new Error('Organization not found');
  }

  const org = orgSnap.data() as Organization;
  const usageLog = org.aiQuota?.usageLog || [];

  return {
    tier: status.tier,
    period: status.period,
    isLifetime: status.isLifetime,
    images: status.images,
    tts: status.tts,
    videos: status.videos,
    recentUsage: usageLog.length > 0 ? usageLog.slice(-10).reverse() : []
  };
};

/**
 * Upgrade an organization to Ministry tier
 * Resets quota to fresh monthly allowance
 */
export const upgradeToMinistry = async (orgId: string, upgradedBy: string): Promise<void> => {
  try {
    const db = getFirestore();
    const orgRef = doc(db, 'organizations', orgId);
    const orgSnap = await getDoc(orgRef);

    if (!orgSnap.exists()) {
      throw new Error('Organization not found');
    }

    const currentMonth = getCurrentMonth();
    const now = Date.now();

    // Reset quota to fresh monthly allowance
    const newQuota: AIQuota = {
      period: currentMonth,
      imageCount: 0,
      ttsCount: 0,
      videoCount: 0,
      lastResetAt: now,
      usageLog: []
    };

    await updateDoc(orgRef, {
      subscription: {
        tier: 'ministry',
        upgradedAt: now,
        upgradedBy: upgradedBy
      },
      aiQuota: newQuota
    });

    console.log(`Organization ${orgId} upgraded to Ministry tier by ${upgradedBy}`);
  } catch (error) {
    console.error('Error upgrading organization:', error);
    throw error;
  }
};

/**
 * Downgrade an organization to Free tier
 * Resets quota to fresh lifetime allowance
 */
export const downgradeToFree = async (orgId: string): Promise<void> => {
  try {
    const db = getFirestore();
    const orgRef = doc(db, 'organizations', orgId);
    const orgSnap = await getDoc(orgRef);

    if (!orgSnap.exists()) {
      throw new Error('Organization not found');
    }

    const now = Date.now();

    // Reset quota to fresh lifetime allowance
    const newQuota: AIQuota = {
      period: 'lifetime',
      imageCount: 0,
      ttsCount: 0,
      videoCount: 0,
      lastResetAt: now,
      usageLog: []
    };

    await updateDoc(orgRef, {
      subscription: {
        tier: 'free'
      },
      aiQuota: newQuota
    });

    console.log(`Organization ${orgId} downgraded to Free tier`);
  } catch (error) {
    console.error('Error downgrading organization:', error);
    throw error;
  }
};

/**
 * Get organization's subscription status
 */
export const getSubscriptionStatus = async (orgId: string): Promise<{
  tier: SubscriptionTier;
  isLifetime: boolean;
  upgradedAt?: number;
  upgradedBy?: string;
  limits: { images: number; tts: number; videos: number };
}> => {
  try {
    const db = getFirestore();
    const orgRef = doc(db, 'organizations', orgId);
    const orgSnap = await getDoc(orgRef);

    if (!orgSnap.exists()) {
      throw new Error('Organization not found');
    }

    const org = orgSnap.data() as Organization;
    const tier = getOrgTier(org);
    const limits = TIER_LIMITS[tier];

    return {
      tier,
      isLifetime: limits.isLifetime,
      upgradedAt: org.subscription?.upgradedAt,
      upgradedBy: org.subscription?.upgradedBy,
      limits: { images: limits.images, tts: limits.tts, videos: limits.videos }
    };
  } catch (error) {
    console.error('Error getting subscription status:', error);
    throw error;
  }
};
